import {Component, Attribute} from "@angular/core";
import {ErrorType, Scilearn} from "../constants";
import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {RecordingWCPM, ReviewWord} from "./reports.objects";
import {RecordingBadge, ReviewWordColor} from "../services/usermodel/usermodel.objects";
import {ReportsService} from "../services/reports/reports.service";
import {FluencyResponse, StoryUserModel} from "./reports.objects";
import {ActivityModalEvent, ACTIVITY_STATUS, ActivityEventData, ReportEventData} from "../event/ActivityEvent";
import {ContentCursor} from "../services/content/content.cursor";
import {UserModelChangedEvent} from "../event/UserModelEvents";
import {UserModelService} from "../services/usermodel/usermodel.service";
import {ComponentBase} from "../base/ComponentBase";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {UtilitiesService} from "../services/utilities.service";
import {TranslateService} from "@ngx-translate/core";
import {FluencyReportAudio} from "./fluency-report-audio";
import {RaVersion} from "../services/configuration/ra.version";
import {ContentFormatter} from "../content/content.formatter";
import {ContentCssClass} from "../content/content-view.objects";
import {Login} from "../login/login";
import {PleaseWaitEvent} from "../event/ContentEvents";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {filter} from "rxjs/operators";
import { ClientConfiguration } from "../services/configuration/client.config";

@Component({
    selector: "fluency-modal",
  templateUrl: "./reports.fluency.modal.component.html"
})
export class FluencyModalComponent extends ComponentBase {

    private modalType:string = Scilearn.FLUENCY_MODAL;
    private GOALHEIGHT: number = 100;
    private GOAL_TOP_BUFFER: number = 122;
    private bottomGoalLinePos: number = 400;
    private MAXBARS: number = 5;
    private justCompletedRecording: boolean = false;

    public activateModal:boolean = false;
    public report:StoryUserModel = <StoryUserModel>{recordingWCPMs: [], reviewWords: []};
    public learnerName:string = "";
    public goal:number;
    public message: string;
    public reviewWordsRange: Array<boolean>;
    public coverImage?: string;

    constructor(private modalConfig:ModalConfig,
                private eventBus:EventBus,
                private contentCursor:ContentCursor,
                private userModelService:UserModelService,
                @Attribute("format") public format:string,
                private login: Login,
                private reportsService:ReportsService,
                private log:BugsnagLoggingService,
                private reportService: ReportsService,
                private translateService: TranslateService,
                private fluencyReportAudio: FluencyReportAudio,
                private audioQueueService: AudioQueueService,
                private config: ClientConfiguration
    ) {
        super("FluencyModalComponent");
        this.subscribeModalObserve();
    }

    private subscribeModalObserve(): void {
        // event filter for user clicking a link to display
        let activitySubscription = this.eventBus.pipe(filter(event => event instanceof ActivityModalEvent)).subscribe((event:ActivityModalEvent) => {
            if (event.status === ACTIVITY_STATUS.modal_open && event.data.type === this.modalType) {
                this.justCompletedRecording = false;
                if (event.data.storyLoaded) {
                    this.openModalWithStoryLoaded();
                } else {
                    this.openModalWithNoStoryLoaded(event.data);
                }
            }
        });

        // event filter for automatically displaying when a recording is complete
        let recordingSubscription = this.eventBus.pipe(filter(event => event instanceof UserModelChangedEvent))
            .subscribe((event:UserModelChangedEvent) => {
            if (event.delta.justCompletedRecording) {
                this.justCompletedRecording = true;
                this.openModalWithStoryLoaded();
            }
        });

        this.subscriptions.push(activitySubscription);
        this.subscriptions.push(recordingSubscription);
    }

    private getProgressHeight(wcpm: number): number {
        let height = (wcpm * this.GOALHEIGHT) / this.goal + 30;
        return height > 250 ? 250 : height;
    }

    public determineMessageKey(recordingWCPMs: RecordingWCPM[], goal: number): string {
        let messageKey = "";
        let recordingCount: number = recordingWCPMs.length;
        let isPrevReached: boolean = false;

        // get most recent recording count
        let isCurrReached: boolean = recordingWCPMs[recordingCount - 1].wcpm >= goal;

        // get recording count before most recent
        if (recordingCount === 2) {
            isPrevReached = recordingWCPMs[recordingCount - 2].wcpm >= goal;
        }

        switch (recordingCount) {
            case 1:
                messageKey = isCurrReached ? "RA_RPT_FLUENCY_07" : "RA_RPT_FLUENCY_04";
                break;
            case 2:
                messageKey = isCurrReached || isPrevReached ? "RA_RPT_FLUENCY_16" : "RA_RPT_FLUENCY_17";
                break;
            default:
                messageKey = this.userModelService.hasRecordingStar ? "RA_RPT_FLUENCY_13" : "RA_RPT_FLUENCY_18";
                break;
        }

        return messageKey;
    }

    public closeModal(): void {
        this.audioQueueService.reset().subscribe(() => {
            this.activateModal = false;
            this.modalConfig.removeModal(
                new ActivityEventData({type: this.modalType, automatic: this.justCompletedRecording}
                ));
            if (this.justCompletedRecording) {
                this.fluencyReportAudio.playClosingAudio();
            }
        });
    }

    public openModal(storyUniqName: string): void {
        this.audioQueueService.reset();
        this.learnerName = this.login.name;
        this.activateModal = true;
        this.showReport(storyUniqName);
        this.reviewWordsRange = this.reportsService.getTableListRange();
    }

    public openModalWithNoStoryLoaded(data: ActivityEventData): void {
        let storyUniqName = data.storyUniqName;
        this.goal = data.fluencyGoal;
        this.coverImage = data.coverImage;
        this.openModal(storyUniqName);
    }

    public openModalWithStoryLoaded(): void {
        let storyUniqName = this.contentCursor.selection.referenceStory();
        this.goal = this.contentCursor.selection.wpmGoal();
        this.coverImage = this.contentCursor.selection.metaData.coverImage;
        this.openModal(storyUniqName);
    }

    // TODO: words do not show in word colors
    public showReport(title:string):void {
        this.log.log("retrieving fluency report for: " + title + " and user: " + this.learnerName);
        let sub = this.reportsService.retrieveFluencyReport(title)
            .subscribe((output:FluencyResponse) => {
                this.getStoryUserModelFromFluencyResponse(output.StoryUserModel);
                if (this.justCompletedRecording) {
                    this.fluencyReportAudio.playAppropriateAudio(this.report, this.goal);
                }
            }, (error) => {
                this.log.error("Error retrieving fluency report: " + error.name, ErrorType.CONNECTION);
                this.modalConfig.openMessageModal("exit_while_offline");
            });
        this.subscriptions.push(sub);
    }

    public getDisplayRecordings(): RecordingWCPM[] {
        return this.report.recordingWCPMs;
    }

    getStoryUserModelFromFluencyResponse(storyUserModel: StoryUserModel): void {
        this.report = storyUserModel;
        this.report.reviewWords = this.reportService.filterOutDuplicateWords(this.report.reviewWords);
        this.report.recordingWCPMs = this.determineDisplayRecordings(this.report.recordingWCPMs);

        this.getMessage();
    }

    determineDisplayRecordings(recordingWCPMs: RecordingWCPM[]): RecordingWCPM[] {

        // then we only want to show 3 at most
        let MAXBARS: number = 3;

        // it is super weird to me but the server-side grabs all the recordings for this selection and
        // sorts by id desc which means the latest version first.  I think we just want to do the opposite.
        let displayRecordings: RecordingWCPM[] = recordingWCPMs.reverse();

        // only return last 3 recordings at the most
        let recordingSize:number = displayRecordings.length;
        if (recordingSize <= MAXBARS) {
            return displayRecordings.slice();
        } else {
            // a size of 4 should return [1, 2, 3]
            // a size of 5 should return [2, 3, 4]
            return displayRecordings.slice(recordingSize - MAXBARS, recordingSize);
        }

    }

    public addOrange(wcpm): boolean {
        return wcpm.wcpm > this.goal;
    }

    public typeMark(input, type): {"fullMark": boolean, "superMark": boolean} {
        let point = (type === "fluency") ? input.wcpm : input.point;
        return {fullMark: point === input.goal, superMark: point > input.goal};
    }

    public getRecordingBadge(): {"img-bar-silver-star": boolean, "img-bar-checkmark": boolean, "img-bar-gold-star": boolean} {
        return {
            "img-bar-silver-star": this.report.recordingBadge === RecordingBadge.SILVER,
            "img-bar-checkmark": this.report.recordingBadge === RecordingBadge.CHECKMARK,
            "img-bar-gold-star": this.report.recordingBadge === RecordingBadge.GOLDEN
        };
    }

    // TODO: Remove this if not ultimately used
    //public getDimensionModification(): {"height": string} {
    //    return {"height": window.innerHeight - 50 + "px"};
    //}

    public printReport(): void {
        this.reportsService.printReport("fluency-main-content");
    }

    public getMessage(): void {
        if (this.report.recordingWCPMs.length === 0) {
            this.message = "";
        }

        let messageKey = this.determineMessageKey(this.report.recordingWCPMs, this.goal);
        this.translateService.get(messageKey).subscribe((res) => {
            this.message = res;
        });
    }

    public determineLeftPivot(index, buffer = 0): string {
        let windowBuffer = window.innerWidth <= 991 ? 30 : 0;
        let pivot = (index * 100) + 95 + buffer + windowBuffer;
        return pivot + "px";
    }

    public determineBaseWidth(report): string {
        return "500px";
    }

    public getBarStyle(recording, index): {"height": string, "top": string, "left": string} {
        let height = 0;
        let top = this.bottomGoalLinePos - 100;

        if (recording) {
            height = this.getProgressHeight(recording.wcpm);
            top = top - this.getProgressHeight(recording.wcpm);
        }
        return {
            "height": height + "px",
            "left": this.determineLeftPivot(index),
            "top": top + "px"
        };
    }

    public getBarDateStyle(index:number): {"position": string, "left": string, "top": string, "font-weight": string} {
        let boldValue = this.getDisplayRecordings().length === (index + 1) ? "bold" : "normal";

        return {
            "position": "absolute",
            "left": this.determineLeftPivot(index, 5),
            "top": "0px",
            "font-weight": boldValue
        };
    }

    public barBottom(recording): string {
        if (recording) {
            return this.getProgressHeight(recording.wcpm) - 17 + "px";
        }
        return "0px";
    }

    public parseDate(date: string): string {
        return UtilitiesService.parseDate(date);
    }

    public rowStripe(index:number):string {
        return UtilitiesService.rowStripe(index);
    }

    public wordColorClass(wordColor:ReviewWordColor):ContentCssClass {
        return ContentFormatter.wordColorToCssClass(wordColor);
    }

    public getGoalLinePosition(): {"top": string} {
        let top = this.bottomGoalLinePos - this.GOAL_TOP_BUFFER;

        if (this.goal) {
            top = top - this.getProgressHeight(this.goal);
        }
        return {"top": top + "px"};
    }

    public getReviewWords(): ReviewWord[] {
        return this.reportsService.outputBufferedList(10, this.report.reviewWords);
    }

    public getReviewWordsTable(index: number): ReviewWord[] {
        return this.reportsService.getReviewWordsTable(index, this.report.reviewWords);
    }

    public belowWordsPracticeGutter(): boolean {
        return window.innerWidth < 991;
    }

    public getCurrentBar(index: number): {"current": boolean} {
        return {
            "current": this.getDisplayRecordings().length === (index + 1)
        };
    }

    public showFirework(recording: RecordingWCPM, index: number): boolean {
        let goalPassed = recording.wcpm > this.goal;
        let latestRecording = this.getDisplayRecordings().length === (index + 1);
        return goalPassed && latestRecording;
    }

    public getFireworksPositioning(recording: RecordingWCPM): {"bottom": string, "left": string} {
        let recordingHeight = this.getProgressHeight(recording.wcpm);
        let goalHeight = this.getProgressHeight(this.goal);
        let bottom = (recordingHeight - goalHeight) + 75;
        return {
            "bottom": bottom + "px",
            "left": "-80px"
        };
    }

    public showRecordBold(index: number): {"font-weight": string} {
        let current = this.getDisplayRecordings().length === (index + 1);
        let value = current ? "bold" : "normal";
        return { "font-weight": value };
    }

    getCoverImage(): string {
        return this.config.introImageUrl(this.coverImage);
    }

}
